import { GiveawayDetailed, Prize } from '@/entities/giveaway/types';
import styles from './GiveawayPrizesActive.module.scss';
import { TelegramSticker } from '@/components/TelegramSticker';
import SratsIcon from '@/assets/stars_gift.png';
import { getPlaceContent } from '@/features/giveaway/place-content/utils';
import {
  sortPrizesByStartRange,
  getPrizeAction,
} from '@/features/giveaway/prize-actions/utils';
import { DisplayContent } from '@/features/giveaway/place-content/types';
import { memo, useCallback } from 'react';
import cleanUrl from '@/utils/cleanUrl';
import { useWordDeclension } from '@/utils/wordDeclension';

interface GiveawayPrizesActiveProps {
  giveaway: GiveawayDetailed;
}

const GiveawayPrizesActive = ({ giveaway }: GiveawayPrizesActiveProps) => {
  const PrizeItem = memo(({ item }: { item: Prize }) => {
    const isStars = item.currency_type === 'stars';
    const isNft = item.currency_type === 'nfts' && item.image_url_with_bg;

    return (
      <div className={isStars ? styles.starsPrize : styles.nftPrize}>
        <div className={styles.prizeItem__description}>
          {isStars ? (
            <span className={styles.prizeItem__amount}>
              {item.quantity}{' '}
              {useWordDeclension(item.quantity, {
                one: 'звезда',
                few: 'звезды',
                many: 'звезд',
              })}
            </span>
          ) : (
            <>
              <span className={styles.prizeItem__name}>{item.name}</span>
              <span className={styles.prizeItem__number}>
                #{item.serial_number}
              </span>
            </>
          )}
        </div>
        <div className={styles.prizeItem__icon}>
          {isStars ? (
            <img src={SratsIcon} alt="stars" />
          ) : isNft ? (
            <TelegramSticker
              tgsUrl={cleanUrl(item.image_url_with_bg!)}
              style={{ width: 200, height: 200 }}
              loop={true}
              autoplay={true}
            />
          ) : null}
        </div>
      </div>
    );
  });

  const renderPlaceContent = useCallback((display: DisplayContent) => {
    if (typeof display === 'string') {
      return display;
    }

    if (display.type === 'image') {
      const IconComponent = display.src;
      return <IconComponent />;
    }

    return null;
  }, []);

  return (
    <div className={styles.prizeList}>
      {sortPrizesByStartRange(
        giveaway.prizes.filter(
          (prize) => prize.start_range >= 0 && prize.end_range >= 0,
        ),
      ).map((item) => {
        const prizeAction = getPrizeAction(item);

        return (
          <div
            key={item.id}
            className={styles.prizeItem}
            onClick={prizeAction.handler}
          >
            <div className={styles.place}>
              {renderPlaceContent(
                getPlaceContent({
                  start: item.start_range,
                  end: item.end_range,
                }).display,
              )}
              <PrizeItem
                item={{
                  ...item,
                  currency_type: item.currency_type as 'stars' | 'nfts',
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default memo(GiveawayPrizesActive);
